import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Loader2, Upload, CheckCircle2, AlertTriangle, XCircle, Download, RefreshCw, Trash2, EyeOff, Undo2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  importBankStatement, listBankImports, getBankReconciliation, confirmBankMatch,
  unmatchBankTransaction, ignoreBankTransaction, rematchBankImport, deleteBankImport,
  bankReconciliationCsv, money,
  type BankReconciliation, type BankStatementImport, type BankTransactionRow,
} from "@/lib/financialLedger";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onChanged: () => void;
}

const CHANNEL_LABEL: Record<string, string> = {
  stripe: "Stripe deposit", zelle: "Zelle", cash: "Cash", check: "Check", ach: "Bank transfer",
  card: "Card", refund: "Refund", payroll: "Payroll", grace: "Grace payment", other: "Other",
};

function BankLine({ b, children }: { b: BankTransactionRow; children?: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell className="text-xs whitespace-nowrap">{b.posted_date}</TableCell>
      <TableCell className="text-xs max-w-[260px]">
        <span className="block truncate">{b.description}</span>
        <span className="text-[11px] text-muted-foreground">{CHANNEL_LABEL[b.channel] ?? b.channel}</span>
      </TableCell>
      <TableCell className="text-right text-xs whitespace-nowrap">
        {b.direction === "in" ? "+" : "−"}{money(Math.abs(b.amount_cents))}
      </TableCell>
      <TableCell className="text-xs">
        {b.matched_transaction_ref ? (
          <span className="font-mono text-[11px]">{b.matched_transaction_ref}</span>
        ) : "—"}
        {b.match_reason && <span className="block text-[11px] text-muted-foreground">{b.match_reason}</span>}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">{children}</TableCell>
    </TableRow>
  );
}

export function BankReconciliationDialog({ open, onOpenChange, onChanged }: Props) {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [imports, setImports] = useState<BankStatementImport[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [recon, setRecon] = useState<BankReconciliation | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [openingBalance, setOpeningBalance] = useState("");
  const [accountLabel, setAccountLabel] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadImports = useCallback(async () => {
    try {
      const r = await listBankImports();
      setImports(r.imports);
      if (!activeId && r.imports[0]) setActiveId(r.imports[0].id);
    } catch (e) {
      toast({ title: "Could not load bank statements", description: (e as Error).message, variant: "destructive" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const loadRecon = useCallback(async (id: string) => {
    setLoading(true);
    try {
      setRecon(await getBankReconciliation(id));
    } catch (e) {
      toast({ title: "Could not build the reconciliation", description: (e as Error).message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (open) loadImports(); }, [open, loadImports]);
  useEffect(() => { if (open && activeId) loadRecon(activeId); }, [open, activeId, loadRecon]);

  const handleFile = async (file: File) => {
    setImporting(true);
    try {
      const text = await file.text();
      const cents = openingBalance.trim() ? Math.round(Number(openingBalance.replace(/[$,]/g, "")) * 100) : null;
      const r = await importBankStatement({
        file_text: text,
        file_name: file.name,
        account_label: accountLabel.trim() || undefined,
        opening_balance_cents: Number.isFinite(cents as number) ? cents : null,
      });
      toast({
        title: `${r.imported} bank line${r.imported === 1 ? "" : "s"} imported`,
        description: `${r.matched} matched · ${r.possible} to review · ${r.unmatched} not in the ledger`,
      });
      setActiveId(r.import_id);
      await loadImports();
      onChanged();
    } catch (e) {
      toast({ title: "Import failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    try {
      await fn();
      if (activeId) await loadRecon(activeId);
      onChanged();
    } catch (e) {
      toast({ title: "That didn't work", description: (e as Error).message, variant: "destructive" });
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = () => {
    if (!recon) return;
    const blob = new Blob([bankReconciliationCsv(recon)], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bank-reconciliation-${recon.period.start_date}-to-${recon.period.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const t = recon?.totals;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bank Reconciliation</DialogTitle>
          <DialogDescription>
            Import a monthly bank statement (CSV or OFX/QFX). Each line is matched to the ledger by amount, date and
            wording — Stripe deposits, Zelle, cash, payroll, Grace payments, refunds, supplies and manual expenses.
            Nothing is written into the ledger from a bank file; the bank is evidence, the ledger is the book of record.
          </DialogDescription>
        </DialogHeader>

        {/* Import */}
        <div className="rounded-lg border border-border p-3 space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">Account label (optional)</Label>
              <Input value={accountLabel} onChange={(e) => setAccountLabel(e.target.value)} placeholder="Business checking" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Opening balance (optional)</Label>
              <Input value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} placeholder="$0.00" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Statement file</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.ofx,.qfx,text/csv,text/plain"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              />
              <Button className="w-full" onClick={() => fileRef.current?.click()} disabled={importing}>
                {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading…</> : <><Upload className="h-4 w-4 mr-2" /> Choose CSV or OFX</>}
              </Button>
            </div>
          </div>
          {imports.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {imports.map((i) => (
                <Button
                  key={i.id}
                  size="sm"
                  variant={i.id === activeId ? "default" : "outline"}
                  onClick={() => setActiveId(i.id)}
                >
                  {i.statement_start} – {i.statement_end} · {i.row_count} lines
                </Button>
              ))}
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Matching every bank line against the ledger…
          </div>
        )}

        {recon && t && !loading && (
          <div className="space-y-4">
            {/* Balances */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Bank Balance</p>
                <p className="text-xl font-heading font-bold">{money(t.bank_balance_cents)}</p>
                <p className="text-[11px] text-muted-foreground">{t.bank_line_count} statement lines</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Ledger Balance</p>
                <p className="text-xl font-heading font-bold">{money(t.ledger_balance_cents)}</p>
                <p className="text-[11px] text-muted-foreground">{t.ledger_line_count} ledger transactions</p>
              </div>
              <div className={`rounded-lg border p-3 ${t.difference_cents === 0 ? "border-border" : "border-destructive"}`}>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Difference</p>
                <p className={`text-xl font-heading font-bold ${t.difference_cents === 0 ? "" : "text-destructive"}`}>
                  {money(t.difference_cents)}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {t.difference_cents === 0 ? "Bank and ledger agree" : "Caused by the transactions listed below"}
                </p>
              </div>
            </div>

            {recon.balanced ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>This statement reconciles</AlertTitle>
                <AlertDescription>Every bank line is matched to a ledger transaction and the difference is $0.00.</AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Not reconciled yet</AlertTitle>
                <AlertDescription>
                  {t.possible_count} to review · {t.missing_from_ledger_count} missing from the ledger ·{" "}
                  {t.missing_from_bank_count} missing from the bank.
                </AlertDescription>
              </Alert>
            )}

            {recon.causes.length > 0 && (
              <div className="rounded-lg border border-border p-3">
                <p className="text-sm font-medium mb-2">Exactly what causes the difference</p>
                <ul className="text-xs space-y-1">
                  {recon.causes.map((c, i) => (
                    <li key={i} className="flex flex-wrap gap-2">
                      <span className="text-muted-foreground">{c.date}</span>
                      <span className="font-medium">{c.description}</span>
                      <span>{money(Math.abs(c.amount_cents))}</span>
                      {c.transaction_ref && <span className="font-mono text-[11px]">{c.transaction_ref}</span>}
                      <span className="text-muted-foreground">— {c.detail}</span>
                      {c.effect_cents !== 0 && (
                        <span className="text-destructive">({c.effect_cents > 0 ? "+" : "−"}{money(Math.abs(c.effect_cents))} of the difference)</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Tabs defaultValue="matched">
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="matched">✅ Matched <Badge variant="secondary" className="ml-1.5">{t.matched_count}</Badge></TabsTrigger>
                <TabsTrigger value="possible">⚠️ Possible <Badge variant="secondary" className="ml-1.5">{t.possible_count}</Badge></TabsTrigger>
                <TabsTrigger value="missing_ledger">❌ Missing from ledger <Badge variant="secondary" className="ml-1.5">{t.missing_from_ledger_count}</Badge></TabsTrigger>
                <TabsTrigger value="missing_bank">❌ Missing from bank <Badge variant="secondary" className="ml-1.5">{t.missing_from_bank_count}</Badge></TabsTrigger>
                {t.ignored_count > 0 && <TabsTrigger value="ignored">Excluded <Badge variant="secondary" className="ml-1.5">{t.ignored_count}</Badge></TabsTrigger>}
              </TabsList>

              <TabsContent value="matched">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead><TableHead>Bank description</TableHead>
                    <TableHead className="text-right">Amount</TableHead><TableHead>Ledger transaction</TableHead><TableHead />
                  </TableRow></TableHeader>
                  <TableBody>
                    {recon.matched.map((b) => (
                      <BankLine key={b.id} b={b}>
                        <Button size="sm" variant="ghost" disabled={busyId === b.id}
                          onClick={() => act(b.id, () => unmatchBankTransaction(b.id))}>
                          <Undo2 className="h-3.5 w-3.5 mr-1" /> Unmatch
                        </Button>
                      </BankLine>
                    ))}
                    {recon.matched.length === 0 && <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground">Nothing matched yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="possible" className="space-y-3">
                {recon.possible.length === 0 && <p className="text-xs text-muted-foreground py-4">No lines need review.</p>}
                {recon.possible.map((b) => (
                  <div key={b.id} className="rounded-lg border border-border p-3 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium">{b.description}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {b.posted_date} · {b.direction === "in" ? "Deposit" : "Withdrawal"} {money(Math.abs(b.amount_cents))} · {b.match_reason}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" disabled={busyId === b.id}
                        onClick={() => act(b.id, () => ignoreBankTransaction(b.id))}>
                        <EyeOff className="h-3.5 w-3.5 mr-1" /> Exclude
                      </Button>
                    </div>
                    <div className="space-y-1">
                      {b.candidate_entry_ids.map((c) => (
                        <div key={c.entry_id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-border/60 px-2 py-1.5">
                          <span className="text-xs">
                            <span className="font-mono text-[11px] mr-2">{c.transaction_ref ?? "—"}</span>
                            {c.transaction_date} · {c.description || c.category} · {money(c.amount_cents)}
                            <span className="text-muted-foreground"> — {c.reason} ({c.score}% confidence)</span>
                          </span>
                          <Button size="sm" disabled={busyId === b.id}
                            onClick={() => act(b.id, () => confirmBankMatch(b.id, c.entry_id))}>
                            This is the match
                          </Button>
                        </div>
                      ))}
                      {b.candidate_entry_ids.length === 0 && (
                        <p className="text-xs text-muted-foreground">No ledger transaction close enough to suggest.</p>
                      )}
                    </div>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="missing_ledger">
                <p className="text-xs text-muted-foreground py-2">
                  On the bank statement but nowhere in the ledger. Record each one as a transaction (or exclude it if it
                  is not Hub money), then re-match.
                </p>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead><TableHead>Bank description</TableHead>
                    <TableHead className="text-right">Amount</TableHead><TableHead>Ledger transaction</TableHead><TableHead />
                  </TableRow></TableHeader>
                  <TableBody>
                    {recon.missing_from_ledger.map((b) => (
                      <BankLine key={b.id} b={b}>
                        <Button size="sm" variant="ghost" disabled={busyId === b.id}
                          onClick={() => act(b.id, () => ignoreBankTransaction(b.id))}>
                          <EyeOff className="h-3.5 w-3.5 mr-1" /> Exclude
                        </Button>
                      </BankLine>
                    ))}
                    {recon.missing_from_ledger.length === 0 && <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground">Nothing missing.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="missing_bank">
                <p className="text-xs text-muted-foreground py-2">
                  Recorded in the ledger but not seen on this statement — often cash not yet deposited, or a payment that
                  cleared in a different month.
                </p>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead><TableHead>Transaction ID</TableHead><TableHead>Description</TableHead>
                    <TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {recon.missing_from_bank.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell className="text-xs whitespace-nowrap">{e.transaction_date}</TableCell>
                        <TableCell className="font-mono text-[11px]">{e.transaction_ref ?? "—"}</TableCell>
                        <TableCell className="text-xs max-w-[260px]"><span className="block truncate">{e.description}</span></TableCell>
                        <TableCell className="text-xs">{e.category}</TableCell>
                        <TableCell className="text-right text-xs">
                          {e.direction === "in" ? "+" : "−"}{money(e.amount_cents)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {recon.missing_from_bank.length === 0 && <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground">Nothing missing.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="ignored">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead><TableHead>Bank description</TableHead>
                    <TableHead className="text-right">Amount</TableHead><TableHead>Note</TableHead><TableHead />
                  </TableRow></TableHeader>
                  <TableBody>
                    {recon.ignored.map((b) => (
                      <BankLine key={b.id} b={b}>
                        <Button size="sm" variant="ghost" disabled={busyId === b.id}
                          onClick={() => act(b.id, () => ignoreBankTransaction(b.id, undefined, true))}>
                          <Undo2 className="h-3.5 w-3.5 mr-1" /> Put back
                        </Button>
                      </BankLine>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {activeId && (
            <>
              <Button variant="outline" size="sm" onClick={() => act(activeId, () => rematchBankImport(activeId))}>
                <RefreshCw className="h-4 w-4 mr-1" /> Re-match
              </Button>
              <Button variant="outline" size="sm" onClick={exportCsv} disabled={!recon}>
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => act(activeId, async () => {
                  await deleteBankImport(activeId);
                  setActiveId(null); setRecon(null); await loadImports();
                })}
              >
                <Trash2 className="h-4 w-4 mr-1" /> Remove statement
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
